import { useEffect, useState } from "react";
import { isStudioPreview } from "@/lib/studioPreviewBridge";
import { fetchTemplateSections, peekTemplateSections, primeTemplateSections } from "@/lib/themeSections";
import { DEFAULT_ENGINE } from "@/themes/registry";

// Cache et déduplication des requêtes vivent désormais dans
// lib/themeSections.js, partagés avec Home.jsx et TemplateSections.jsx.

// Sprint 11 — lecture des sections d'un template "chrome" (header/footer),
// partagée par Layout.jsx (barre d'annonce) et Footer.jsx. Retourne le
// tuple [sections, loaded] :
//   - sections null  + loaded false  → encore en cours de chargement
//   - sections null  + loaded true   → API en échec (repli sur les défauts
//                                      codés en dur pour garder la page
//                                      présentable malgré l'erreur)
//   - sections array + loaded true   → chargé avec succès
// Séparer "chargement" de "échec" évite le flash du contenu d'agence par
// défaut (ClickExpert, liens agence…) qui apparaissait brièvement avant que
// l'API réponde avec le vrai contenu de la boutique.
//
// Troisième valeur du tuple : le MOTEUR de thème de la boutique (voir
// frontend/src/themes/registry.js). Ajout purement additif — un appelant
// qui déstructure `[sections, loaded]` comme avant est inchangé. Layout.jsx
// s'en sert pour savoir si le moteur rend lui-même l'en-tête et le pied de
// page, ou s'il faut garder le chrome partagé.
export function useTemplateSections(template) {
  const preview = isStudioPreview();
  // Lecture synchrone du cache partagé : si la réponse est déjà connue, le
  // premier rendu est immédiat et aucune requête n'est émise.
  const cached = peekTemplateSections(template);
  const [sections, setSections] = useState(cached ? cached.sections : null);
  const [engine, setEngine] = useState(cached ? cached.engine : DEFAULT_ENGINE);
  const [loaded, setLoaded] = useState(!!cached);

  useEffect(() => {
    let active = true;
    // `fetchTemplateSections` se raccroche à une requête déjà en vol pour le
    // même template : deux composants montés ensemble (Layout et Footer
    // demandent tous deux "footer") n'en déclenchent plus qu'une.
    fetchTemplateSections(template).then((result) => {
      if (!active) return;
      setSections(result.sections);
      setEngine(result.engine);
      setLoaded(true);
    });
    return () => { active = false; };
  }, [template]);

  useEffect(() => {
    if (!preview) return undefined;
    const onDraft = (e) => {
      if (e.detail?.template !== template) return;
      const s = e.detail.sections || [];
      primeTemplateSections(template, s, engine);
      setSections(s);
    };
    window.addEventListener("studio:sections", onDraft);
    return () => window.removeEventListener("studio:sections", onDraft);
  }, [template, preview, engine]);

  return [sections, loaded, engine];
}

export function findEnabledSection(sections, type) {
  return (sections || []).find((s) => s.type === type && s.enabled !== false) || null;
}

// Lit un champ "checkbox" ("true"/"false", voir theme_service.FIELD_TYPES)
// d'une section. Accepte `loaded` (second résultat de useTemplateSections) :
//   - !loaded         → false (pas encore chargé, rien n'est affiché)
//   - loaded + null   → defaultValue (API en échec, repli sur le défaut)
//   - loaded + array  → lit la valeur réelle du champ
// N'utiliser que pour un type de TEMPLATE_DEFAULT_ORDER (aujourd'hui :
// "header_menu" uniquement) — un tel type est TOUJOURS présent dans le
// tableau chargé dès que ce thème n'a jamais enregistré son template, donc
// son absence une fois chargé ne peut signifier qu'une suppression
// (ancienne corbeille) et doit retomber sur `false`, jamais `defaultValue`.
export function sectionFlag(sections, type, key, defaultValue = true, loaded = true) {
  if (!loaded) return false;
  if (sections === null) return defaultValue;
  const section = findEnabledSection(sections, type);
  if (!section) return false;
  const value = section.props?.[key];
  if (value === undefined) return defaultValue;
  return value !== "false";
}
